import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/common';
import { ApiRequestError, errorMessage } from '@/lib/api-client';
import type { IcdRef, PatientDetail, Visit } from '@/types/api';
import {
  mergePending,
  type PendingRecord,
  type RecordPatch,
  recordPatch,
} from '@/utils/visitRecord';
import { asSentence, type VisitRecordPart } from '../../dictation';
import { patientsApi } from '../../patients/api';
import { visitsApi } from '../api';

export type { PendingRecord, RecordPatch };

/**
 * Speech pieces of one utterance arrive up to 2.5 s apart (see finalFilter):
 * what is said is saved once it has been quiet a little longer, as one sentence.
 */
const SETTLE_MS = 2600;

/** The catalogue's entry for each code; codes it does not know are left out. */
async function knownCodes(
  codes: readonly string[]
): Promise<{ known: IcdRef[]; unknown: string[] }> {
  const known: IcdRef[] = [];
  const unknown: string[] = [];
  for (const code of codes) {
    const found = (await patientsApi.searchIcd(code)).find((r) => r.code === code);
    if (found) known.push(found);
    else unknown.push(code);
  }
  return { known, unknown };
}

/**
 * The visit's protocol record (#23): the anamnesis and diagnoses, edited by
 * hand or dictated ("anamneza …", "dijagnoza K02.1"). Dictated parts go to
 * `open`, the open visit shown now, once it exists and the speaker paused.
 */
export function useVisitRecord(
  setPatient: Dispatch<SetStateAction<PatientDetail | undefined>>,
  open: Visit | null
) {
  const { t } = useTranslation();
  const toast = useToast();
  const [pending, setPending] = useState<PendingRecord | null>(null);

  const save = useCallback(
    async (visit: Visit, patch: RecordPatch) => {
      try {
        const updated = await visitsApi.setRecord(visit.id, patch);
        setPatient((p) =>
          p ? { ...p, visits: p.visits.map((v) => (v.id === updated.id ? updated : v)) } : p
        );
        return true;
      } catch (err) {
        toast.show(
          err instanceof ApiRequestError && err.isNetwork
            ? t('chart.toast.needsOnline')
            : errorMessage(err),
          'error'
        );
        return false;
      }
    },
    [setPatient, toast, t]
  );

  const add = useCallback((part: VisitRecordPart) => {
    setPending((p) => ({
      anamnesis: [...(p?.anamnesis ?? []), ...(part.anamnesis ? [part.anamnesis] : [])],
      diagnoses: [
        ...(p?.diagnoses ?? []),
        ...(part.diagnoses ?? []).filter((c) => !p?.diagnoses.includes(c)),
      ],
      failed: false,
    }));
  }, []);

  // One save at a time: the next one starts from the visit the previous one returned.
  const [flushing, setFlushing] = useState(false);

  /** Saves what was dictated into `visit`, after what it already has. */
  const flush = useCallback(
    async (visit: Visit, record: PendingRecord) => {
      setPending(null);
      setFlushing(true);
      const keep = () =>
        // Kept on screen, not retried by itself (offline it would fail every time).
        setPending((p) => mergePending(record, p, true));
      try {
        const codes = record.diagnoses.filter((c) => !visit.diagnoses.some((d) => d.code === c));
        let found: Awaited<ReturnType<typeof knownCodes>>;
        try {
          found = await knownCodes(codes);
        } catch {
          toast.show(t('chart.toast.needsOnline'), 'error');
          keep();
          return;
        }
        if (found.unknown.length) {
          const codesText = found.unknown.join(', ');
          toast.show(t('chart.visitRecord.unknownCodes', { codes: codesText }), 'error');
        }
        const patch = recordPatch(visit, asSentence(record.anamnesis.join(' ')), found.known);
        if (patch && !(await save(visit, patch))) keep();
      } finally {
        setFlushing(false);
      }
    },
    [save, toast, t]
  );

  const latest = useRef({ open, flush });
  latest.current = { open, flush };
  useEffect(() => {
    if (flushing || !pending || pending.failed || open?.status !== 'OPEN') return;
    const timer = setTimeout(() => {
      const { open: visit, flush: run } = latest.current;
      if (visit) void run(visit, pending);
    }, SETTLE_MS);
    return () => clearTimeout(timer);
  }, [pending, open, flushing]);

  /** Save the pending part now (the retry after a failed save). */
  const retry = useCallback(() => {
    if (pending && open && !flushing) void flush(open, pending);
  }, [pending, open, flushing, flush]);

  return { pending, add, save, retry };
}
